import type { ErrorsEmitter } from '@realadvisor/error';
import type {
  FetchFunction,
  IEnvironment,
  RequestParameters,
  Variables,
} from 'relay-runtime';

/** What `environment.getStore().getSource()` actually returns. */
type RelayRecordSource = ReturnType<
  ReturnType<IEnvironment['getStore']>['getSource']
>;

import { shouldRetryAfterRefresh } from './jwt-retry';
import { getSessionToken, refreshSessionOrReload } from './session';

/**
 * The Relay fetch function, in its own module for the same reason
 * `jwt-retry.ts` is: reaching it from a test otherwise means importing React,
 * `react-relay` and — through `errorsEmitter` — Apollo.
 *
 * What is worth a test here is the terminating condition of the retry, which
 * this PR changed the meaning of. See `runQuery`.
 */
export const createFetchQuery = ({
  endpoint,
  errorsEmitter,
  getRecordSource,
}: {
  endpoint: string;
  errorsEmitter: ErrorsEmitter;
  /**
   * The api environment's store, read only for the `me` fixup below. A getter
   * rather than the store itself because the environment does not exist yet
   * when this function is built — it is built to create it.
   */
  getRecordSource: () => RelayRecordSource | undefined;
}): FetchFunction => {
  // `isRetry` is the terminating condition, and it has to be carried rather
  // than re-derived.
  //
  // This function retries by calling itself. That was safe while the only
  // trigger was `realadvisor.api`'s `JWT is expired`: a successful refresh
  // always produces a token that endpoint accepts, so the recursion could not
  // go round twice. `shouldRetryAfterRefresh` is strictly broader — in webhook
  // mode every hook refusal arrives as one `access-denied` wording, including
  // causes a refresh does not fix — so "a refresh fixes it" stopped being a
  // terminating condition at all.
  //
  // Without this flag, any persistent hook refusal that `/auth/refresh` does
  // NOT also refuse loops forever at two network round-trips per iteration,
  // from every open tab: a user-level cutoff against a token that carries no
  // `iat`, clock skew on `iat`, a sidecar failing closed. That is a request
  // storm against the auth service at exactly the moment you are revoking in
  // bulk after a takeover — the incident this stack exists for.
  //
  // One retry per operation, which is what the Apollo `ErrorLink` already gets
  // by handing its retry to `forward(operation)` rather than re-entering its
  // own handler.
  //
  // Deliberately NOT a `WeakSet` keyed on `operation`: Relay reuses one
  // `RequestParameters` object for every execution of a query, so marking it
  // would disable the refresh for that query for the rest of the session.
  const runQuery = async (
    operation: RequestParameters,
    variables: Variables,
    isRetry: boolean,
  ) => {
    const headers = new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    const access_token = getSessionToken();
    if (access_token != null) {
      headers.set('Authorization', `Bearer ${access_token}`);
    }
    const request: RequestInit = {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        query: operation.text,
        variables,
        name: operation.name,
      }),
    };

    const response = await fetch(endpoint, request);
    const data = await response.json();
    if (data.errors) {
      // Two wordings, because this fetch function is shared by two endpoints
      // that report an expired token differently.
      //
      // `'JWT is expired'` is `realadvisor.api`'s own string (`src/jwt.js`),
      // and that is the endpoint at `api_origin`. `RelayHasuraWrapper` points
      // the same fetch function at `hasura_origin`, which says something else
      // entirely — `invalid-jwt` in JWT mode, and `access-denied` +
      // "Authentication hook unauthorized" once this PR flips it to webhook
      // mode. So the legacy Relay-on-Hasura pages (`legacy/pages/performance`,
      // `legacy/hasura-relay/shared/team-filter`, `UserOverview`) never matched
      // here at all, and after the flip they would surface an error instead of
      // refreshing silently.
      //
      // `shouldRetryAfterRefresh` is the same predicate the Apollo link uses,
      // rather than a second copy of the wordings to keep in step.
      //
      // `!isRetry` comes first so the retry does not also spend a refresh
      // round-trip before giving up.
      if (
        !isRetry &&
        data.errors.some(
          (e: { message: string; extensions?: { code?: unknown } | null }) =>
            e.message.includes('JWT is expired') || shouldRetryAfterRefresh(e),
        ) &&
        (await refreshSessionOrReload())
      ) {
        return runQuery(operation, variables, true);
      } else {
        // A JWT error that cannot be retried surfaces like any other error —
        // and so does one that survived the retry, which is why this branch is
        // what `isRetry` falls into rather than a silent return.
        errorsEmitter.emit('add', operation.name, data.errors);
      }
    }

    if (data.data != null && 'me' in data.data && data.data.me == null) {
      // Check if 'me' object exists in Relay store
      const source = getRecordSource();
      const meRef = (
        source?.get('client:root')?.me as {
          __ref: string;
        } | null
      )?.__ref;
      if (meRef != null) {
        const meRecord = source?.get(meRef);
        if (meRecord != null) {
          data.data.me = meRecord;
        }
      }
    }

    return data;
  };

  return (operation, { ...variables }) => {
    // relay removes directives likes @deleteEdge before passing to fetch
    // though $connections variable is left and hasura fails because of it
    // prevent here passing it to hasura and leave only for relay
    delete variables.connections;

    return runQuery(operation, variables, false);
  };
};
